import { API_URLS } from '../urls';

/**
 * Sondages en direct (2026-10-15) : le créateur prépare et lance un sondage de 2
 * à 4 réponses, les spectateurs connectés de Kleon.tv votent (customer-api), le
 * résultat se montre sur le portail et, au choix, à l'antenne.
 * admin-api, PollController.
 *
 * Relevé périodique, comme « Monter sur le plateau » : les votes viennent du
 * portail, qui n'a pas de canal temps réel vers la régie. `no-store` pour que le
 * relevé voie vraiment le serveur.
 *
 * Réservé aux chaînes créées par la régie web (409 `not_regie` sinon) ; un seul
 * sondage ouvert à la fois par chaîne (409 `busy`).
 */

export type PollStatus = 'draft' | 'open' | 'closed' | 'archived';

export interface PollOption {
  id      : number;
  label   : string;
  correct : boolean;
  votes   : number;
}

export interface Poll {
  id                     : number;
  channelId              : number;
  question               : string;
  status                 : PollStatus;
  /** 60, 120, 300, ou null = sans limite. */
  durationSeconds        : number | null;
  showResults            : boolean;
  quiz                   : boolean;
  onAir                  : boolean;
  createdAt              : string | null;
  openedAt               : string | null;
  closedAt               : string | null;
  /** Secondes avant la clôture d'office (ouvert avec une durée), comptées par le serveur. */
  remainingSeconds       : number | null;
  /** Secondes pendant lesquelles le résultat reste montré (clos). */
  resultRemainingSeconds : number | null;
  totalVotes             : number;
  options                : PollOption[];
}

export interface PollsState {
  channel    : { id: number; active: boolean; createdFromRegie: boolean };
  current    : Poll | null;
  drafts     : Poll[];
  finished   : Poll[];
  serverTime : string;
}

/** Ce que la régie envoie pour préparer ou modifier un sondage. */
export interface PollDraftInput {
  question        : string;
  options         : { label: string; correct: boolean }[];
  durationSeconds : number | null;
  showResults     : boolean;
  quiz            : boolean;
}

export class PollError extends Error {
  constructor(message: string, public readonly status: number, public readonly code?: string) {
    super(message);
  }
}

const readJson = async <T>(res: Response): Promise<T> => {
  const data = await res.json().catch(() => ({}));

  if (!res.ok) throw new PollError(data.error ?? `HTTP ${res.status}`, res.status, data.code);

  return data as T;
};

const postJson = <T>(url: string, body: object): Promise<T> =>
  fetch(url, {
    method  : 'POST',
    headers : { 'Content-Type': 'application/json' },
    body    : JSON.stringify(body),
  }).then(res => readJson<T>(res));

export const fetchPolls = async (adminId: number, channelId: number): Promise<PollsState> => {
  const res  = await fetch(`${API_URLS.getPolls}?adminId=${adminId}&channelId=${channelId}`, { cache: 'no-store' });
  const data = await readJson<PollsState>(res);

  return {
    ...data,
    current  : data.current ?? null,
    drafts   : Array.isArray(data.drafts)   ? data.drafts   : [],
    finished : Array.isArray(data.finished) ? data.finished : [],
  };
};

export const createPoll = (adminId: number, channelId: number, input: PollDraftInput) =>
  postJson<{ poll: Poll }>(API_URLS.createPoll, { adminId, channelId, ...input }).then(d => d.poll);

type PollAction = 'update' | 'open' | 'close' | 'withdraw' | 'on_air' | 'duplicate' | 'delete';

const actionUrl = (id: number, action: PollAction) =>
  API_URLS.pollAction.replace('{id}', String(id)).replace('{action}', action);

export const updatePoll = (adminId: number, id: number, input: PollDraftInput) =>
  postJson<{ poll: Poll }>(actionUrl(id, 'update'), { adminId, ...input }).then(d => d.poll);

/** Lance un sondage préparé ; `onAir` montre aussitôt le bandeau à l'antenne. */
export const openPoll = (adminId: number, id: number, onAir: boolean) =>
  postJson<{ poll: Poll }>(actionUrl(id, 'open'), { adminId, onAir }).then(d => d.poll);

export const closePoll = (adminId: number, id: number) =>
  postJson<{ poll: Poll }>(actionUrl(id, 'close'), { adminId }).then(d => d.poll);

/** « Retirer le résultat » : le sondage clos ne se montre plus, ni sur Kleon.tv ni à l'antenne. */
export const withdrawPoll = (adminId: number, id: number) =>
  postJson<{ poll: Poll }>(actionUrl(id, 'withdraw'), { adminId }).then(d => d.poll);

export const setPollOnAir = (adminId: number, id: number, onAir: boolean) =>
  postJson<{ poll: Poll }>(actionUrl(id, 'on_air'), { adminId, onAir }).then(d => d.poll);

/** « Reprendre » un sondage terminé : une copie préparée, sans voix. */
export const duplicatePoll = (adminId: number, id: number) =>
  postJson<{ poll: Poll }>(actionUrl(id, 'duplicate'), { adminId }).then(d => d.poll);

export const deletePoll = (adminId: number, id: number) =>
  postJson<{ ok: boolean }>(actionUrl(id, 'delete'), { adminId });
